import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import {
  LayoutDashboard, Clock, ClipboardList, Calendar, Receipt, User as UserIcon,
  Users, FileCheck, DollarSign, FileText, Settings as SettingsIcon, History, LogOut, Zap, Percent, Wrench, Database, CreditCard, Building2, Rocket, BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import TrialBanner from "@/components/TrialBanner";

const employeeNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/time", label: "Time Clock", icon: Clock },
  { to: "/jobs", label: "Job Entries", icon: ClipboardList, commissionOnly: true },
  { to: "/week", label: "Weekly", icon: Calendar },
  { to: "/paystubs", label: "Paystubs", icon: Receipt },
  { to: "/reimbursements", label: "Reimbursements", icon: DollarSign, leadOnly: true },
];

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/employees", label: "Employees", icon: Users },
  { to: "/admin/submissions", label: "Submissions", icon: FileCheck },
  { to: "/admin/daily-punches", label: "Daily Punches", icon: Clock },
  { to: "/admin/shop-time", label: "Shop Time", icon: Wrench },
  { to: "/admin/payroll", label: "Payroll", icon: DollarSign },
  { to: "/admin/paystubs", label: "Paystubs", icon: Receipt },
  { to: "/admin/reimbursements", label: "Reimbursements", icon: DollarSign },
  { to: "/admin/reports", label: "Reports", icon: FileText },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
  { to: "/admin/billing", label: "Billing", icon: CreditCard },
  { to: "/admin/taxes", label: "Taxes", icon: Percent, requiresTaxes: true },
  { to: "/admin/audit", label: "Audit Log", displayFor: { admin: "Activity", super_admin: "Audit Log" }, icon: History },
  { to: "/admin/handbook", label: "VA Handbook", icon: BookOpen },
  { to: "/admin/industry", label: "Industry & HVAC", icon: Wrench },
  { to: "/admin/all-users", label: "All Users (raw)", icon: Database, superAdminOnly: true },
  { to: "/admin/tenants", label: "Tenants", icon: Building2, superAdminOnly: true },
  { to: "/growth", label: "Growth Center", icon: Rocket, growthOnly: true, accent: "indigo" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { company_name, logoUrl, taxes_enabled_globally } = useBranding();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const isGrowth = user?.role === "super_admin" || user?.role === "growth_manager";
  const commissionEligible = !!user && user.default_pay_method && user.default_pay_method !== "hourly";
  const isLead = !!user?.is_lead_technician;
  const rawNav = isAdmin ? adminNav : employeeNav;
  const nav = rawNav.filter((item) => {
    if (item.requiresTaxes && !taxes_enabled_globally) return false;
    if (item.commissionOnly && !commissionEligible) return false;
    if (item.leadOnly && !isLead) return false;
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.growthOnly && !isGrowth) return false;
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-white border-r border-slate-200 z-40">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-slate-200">
          <div className="h-12 w-12 rounded-xl bg-sky-600 flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt={company_name} className="h-10 w-10 object-contain" data-testid="sidebar-logo" />
            ) : (
              <Zap className="h-6 w-6 text-white" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 font-display leading-tight truncate" title={company_name}>{company_name}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Pay Tracker</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((item) => {
            const label = item.displayFor?.[user?.role] || item.label;
            return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-sky-50 text-sky-700" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {label}
            </NavLink>
          );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-700">
              {(user?.first_name?.[0] || "U").toUpperCase()}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-slate-500 truncate uppercase tracking-wider">
                {isSuperAdmin ? "Super Admin" : user?.role}
                {isSuperAdmin && <span className="ml-1 text-amber-500">★</span>}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={handleLogout} data-testid="logout-btn-desktop">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-sky-600 flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt={company_name} className="h-8 w-8 object-contain" />
              ) : (
                <Zap className="h-5 w-5 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight font-display truncate">{company_name}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{user?.first_name} {user?.last_name}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn-mobile">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="md:pl-64 pb-24 md:pb-8">
        {user?.role === "admin" && <TrialBanner />}
        <SubscriptionBanner />
        <div className="px-4 sm:px-6 md:px-8 py-4 md:py-8 max-w-5xl md:max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 pb-safe">
        <div className="flex items-stretch justify-around no-scrollbar overflow-x-auto">
          {nav.map((item) => {
            const label = item.displayFor?.[user?.role] || item.label;
            return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`mnav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 px-3 py-2 min-w-[64px] text-[10px] font-semibold uppercase tracking-wider ${
                  isActive ? "text-sky-700" : "text-slate-500"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {label}
            </NavLink>
          );
          })}
        </div>
      </nav>
    </div>
  );
}
